import {
  Box,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import useStyles from "./styles";
import {
  ArrowOutwardOutlined,
  Clear,
  Search,
  ShowChart,
  StackedLineChart,
  WifiOutlined,
} from "@mui/icons-material";
import DatagridComponent from "../../../../../components/datagridComponent";
import { useEffect, useMemo, useState } from "react";
import LineChartComponent from "../../../../../components/lineComponent";
import dayjs from "dayjs";
const timeZone = "America/Sao_Paulo";
/**
 * Modos disponíveis para visualização do gráfico
 */
const CHART_MODE = {
  STACKED: "stacked",
  CONCATENATED: "concatenated",
  MULTILINE: "multiline",
};

export default function LinksBgp({
  links,
  loading,
  handleAplicarZoom = () => {},
  isZoomEnabled = false,
  interfaceDescriptions = {}, // {[interface]: description}
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  /**
   * Texto de busca para filtragem da tabela
   */
  const [search, setSearch] = useState("");

  /**
   * Paleta de cores usada no modo MULTILINE
   * A ordem é determinística e cíclica
   */
  const colors = [
    // Light (primeira volta – mais claros)
    theme?.palette?.colors?.red?.light,
    theme?.palette?.colors?.orange?.light,
    theme?.palette?.colors?.yellow?.light,
    theme?.palette?.colors?.green?.light,
    theme?.palette?.colors?.blue?.light,
    theme?.palette?.colors?.purple?.light,
    theme?.palette?.colors?.pink?.light,

    // Medium (segunda volta – base)
    theme?.palette?.colors?.red?.medium,
    theme?.palette?.colors?.orange?.medium,
    theme?.palette?.colors?.yellow?.medium,
    theme?.palette?.colors?.green?.medium,
    theme?.palette?.colors?.blue?.medium,
    theme?.palette?.colors?.purple?.medium,
    theme?.palette?.colors?.pink?.medium,

    // Dark (terceira volta – contraste)
    theme?.palette?.colors?.red?.dark,
    theme?.palette?.colors?.orange?.dark,
    theme?.palette?.colors?.yellow?.dark,
    theme?.palette?.colors?.green?.dark,
    theme?.palette?.colors?.blue?.dark,
    theme?.palette?.colors?.purple?.dark,
    theme?.palette?.colors?.pink?.dark,

    // Darker (quarta volta – séries extras)
    // theme?.palette?.colors?.red?.darker,
    // theme?.palette?.colors?.orange?.darker,
    // theme?.palette?.colors?.yellow?.darker,
    // theme?.palette?.colors?.green?.darker,
    // theme?.palette?.colors?.blue?.darker,
    // theme?.palette?.colors?.purple?.darker,
    // theme?.palette?.colors?.pink?.darker,
  ];

  /**
   * Interfaces selecionadas na tabela
   */
  const [selectedInterfaces, setSelectedInterfaces] = useState([]);

  useEffect(() => {
    if (links?.interfaces?.length > 0) {
      setSelectedInterfaces(links.interfaces.map((link) => link.interface));
    }
  }, [links?.interfaces]);

  /**
   * Modo atual do gráfico
   */
  const [chartMode, setChartMode] = useState(CHART_MODE.STACKED);

  /**
   * Trata a seleção de linhas da tabela
   */
  const handleSelectionChange = (selectionModel) => {
    const selected = links?.interfaces?.filter((link) =>
      selectionModel?.includes(link.id),
    );
    setSelectedInterfaces(selected?.map((s) => s?.interface) || []);
  };

  const rowSelectionModel = useMemo(() => {
    return (
      links?.interfaces
        ?.filter((link) => selectedInterfaces.includes(link.interface))
        ?.map((link) => link.id) || []
    );
  }, [selectedInterfaces, links?.interfaces]);

  /**
   * Colunas da tabela de interfaces
   */
  const columns = [
    { field: "id", headerName: "ID", hide: true },
    {
      field: "interface",
      headerName: "Interface",
      flex: 2.5,
      renderCell: (params) => {
        const description = interfaceDescriptions?.[params?.value];
        const text = description
          ? `${params?.value} - ${description}`
          : params?.value;

        return (
          <Typography
            variant="body2"
            noWrap
            title={text}
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    },
    {
      field: "trafficInLastItem",
      headerName: "Entrada",
      // <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
      //   Entrada
      //   <ArrowOutwardOutlined
      //     fontSize="12px"
      //     sx={{
      //       transform: "rotate(90deg)",
      //       mt: 0.2,
      //       color: theme.palette.cardGraphic.graphColor.blueMedium,
      //     }}
      //   />
      // </Box>
      flex: 1,
      renderCell: (params) => {
        const text = formatBytes({ bytes: params?.value, precisao: 0 });
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography
            variant="body2"
            noWrap
            title={title}
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    },
    // Estatísticas de Entrada
    {
      field: "minIn",
      headerName: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          Mín
          <ArrowOutwardOutlined
            fontSize="12px"
            sx={{
              transform: "rotate(90deg)",
              mt: 0.2,
              color: theme.palette.cardGraphic.graphColor.blueMedium,
            }}
          />
        </Box>
      ),
      flex: 1,
      renderCell: (params) => {
        const text = formatBytes({ bytes: params?.value, precisao: 0 });
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography
            variant="body2"
            noWrap
            title={title}
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    },
    {
      field: "avgIn",
      headerName: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          Méd
          <ArrowOutwardOutlined
            fontSize="12px"
            sx={{
              transform: "rotate(90deg)",
              mt: 0.2,
              color: theme.palette.cardGraphic.graphColor.blueMedium,
            }}
          />
        </Box>
      ),
      flex: 1,
      renderCell: (params) => {
        const text = formatBytes({ bytes: params?.value, precisao: 0 });
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography
            variant="body2"
            noWrap
            title={title}
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    },
    {
      field: "maxIn",
      headerName: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          Máx
          <ArrowOutwardOutlined
            fontSize="12px"
            sx={{
              transform: "rotate(90deg)",
              mt: 0.2,
              color: theme.palette.cardGraphic.graphColor.blueMedium,
            }}
          />
        </Box>
      ),
      flex: 1,
      renderCell: (params) => {
        const text = formatBytes({ bytes: params?.value, precisao: 0 });
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography
            variant="body2"
            noWrap
            title={title}
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    },
    {
      field: "trafficOutLastItem",
      headerName: "Saída",
      // <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
      //   Saida
      //   <ArrowOutwardOutlined
      //     fontSize="12px"
      //     sx={{
      //       color: theme.palette.cardGraphic.graphColor.orangeMedium,
      //     }}
      //   />
      // </Box>
      flex: 1,
      renderCell: (params) => {
        const text = formatBytes({ bytes: params?.value, precisao: 0 });
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography
            variant="body2"
            noWrap
            title={title}
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    },
    // Estatísticas de Saída
    {
      field: "minOut",
      headerName: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          Mín
          <ArrowOutwardOutlined
            fontSize="12px"
            sx={{
              color: theme.palette.cardGraphic.graphColor.orangeMedium,
            }}
          />
        </Box>
      ),
      flex: 1,
      renderCell: (params) => {
        const text = formatBytes({ bytes: params?.value, precisao: 0 });
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography
            variant="body2"
            noWrap
            title={title}
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    },
    {
      field: "avgOut",
      headerName: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          Méd
          <ArrowOutwardOutlined
            fontSize="12px"
            sx={{
              color: theme.palette.cardGraphic.graphColor.orangeMedium,
            }}
          />
        </Box>
      ),
      flex: 1,
      renderCell: (params) => {
        const text = formatBytes({ bytes: params?.value, precisao: 0 });
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography
            variant="body2"
            noWrap
            title={title}
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    },
    {
      field: "maxOut",
      headerName: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          Máx
          <ArrowOutwardOutlined
            fontSize="12px"
            sx={{
              color: theme.palette.cardGraphic.graphColor.orangeMedium,
            }}
          />
        </Box>
      ),
      flex: 1,
      renderCell: (params) => {
        const text = formatBytes({ bytes: params?.value, precisao: 0 });
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography
            variant="body2"
            noWrap
            title={title}
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    },
  ];

  /**
   * Linhas da tabela de interfaces
   * Mapeia os dados recebidos da API, utilizando conditional chaining para segurança
   */
  const rows =
    links?.interfaces?.map((iface) => {
      const ifaceName = iface?.interface;

      // Acessa os objetos de tráfego com segurança
      const trafficInObj = links?.trafficIn?.[ifaceName] || {};
      const trafficOutObj = links?.trafficOut?.[ifaceName] || {};

      return {
        id: iface?.id,
        interface: ifaceName,

        // Último valor (Atual)
        trafficInLastItem: links?.trafficInLastItem?.[ifaceName]?.value ?? 0,
        trafficOutLastItem: links?.trafficOutLastItem?.[ifaceName]?.value ?? 0,

        // Estatísticas de Entrada (Min, Mean, Max)
        minIn: trafficInObj?.min ?? 0,
        avgIn: trafficInObj?.mean ?? 0, // A API retorna 'mean'
        maxIn: trafficInObj?.max ?? 0,

        // Estatísticas de Saída (Min, Mean, Max)
        minOut: trafficOutObj?.min ?? 0,
        avgOut: trafficOutObj?.mean ?? 0, // A API retorna 'mean'
        maxOut: trafficOutObj?.max ?? 0,
      };
    }) || [];

  const filteredRows = rows.filter((row) => {
    if (!search) return true;

    const searchLower = search.toLowerCase();

    return Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchLower),
    );
  });

  /**
   * Configuração dos gráficos (entrada e saída)
   * Evita repetição de Paper e lógica duplicada
   */
  const graphs = [
    {
      key: "trafficIn",
      title: "Tráfego de entrada",
      titleColor: theme.palette.cardGraphic.graphColor.blue,
    },
    {
      key: "trafficOut",
      title: "Tráfego de saída",
      titleColor: theme.palette.cardGraphic.graphColor.orange,
    },
  ];

  /**
   * Gera o objeto de cores
   */
  const interfaceColorMap = useMemo(() => {
    if (!links?.interfaces?.length) return {};

    return links.interfaces.reduce((acc, iface, idx) => {
      acc[iface.interface] = colors[idx % colors.length];
      return acc;
    }, {});
  }, [links?.interfaces, colors]);

  const objectColorsLine =
    chartMode === CHART_MODE.MULTILINE
      ? selectedInterfaces.reduce((acc, iface) => {
          acc[iface] = interfaceColorMap[iface];
          return acc;
        }, {})
      : false;

  const toggleChartMode = () => {
    setChartMode((prev) =>
      prev === CHART_MODE.STACKED ? CHART_MODE.MULTILINE : CHART_MODE.STACKED,
    );
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Gráficos de links - lado a lado */}
        {graphs?.map((graph, idx) => {
          const datasets = buildDatasets(
            links,
            selectedInterfaces,
            graph?.key,
          );

          let chartData = buildChartData(datasets, chartMode);

          chartData = sortChartDataKeysByMax(
            chartData,
            datasets,
            links,
            graph?.key,
          );

          // Extrai a ordem real das interfaces no chartData (do primeiro item)
          const actualKeysOrder = chartData?.length
            ? Object.keys(chartData?.[0]).filter((k) => k !== "clock")
            : [];

          // Monta stackedGraphColors na ordem EXATA do gráfico
          const stackedGraphColors = actualKeysOrder?.map(
            (key) => interfaceColorMap?.[key],
          );

          return (
            <Grid key={graph?.key} item xs={12} md={6} sx={styles.gridContentChart}>
              <Paper
                elevation={theme?.palette?.mode === "dark" ? 0 : 3}
                sx={{
                  ...styles.paperGraphcs,
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  p: 2,
                }}
              >
                {!!loading && <LinearProgress sx={styles.linearProgress} />}
                <Box sx={styles.boxTitleGraphcs}>
                  <Typography color={graph?.titleColor} variant="subtitle1">
                    {graph?.title}
                  </Typography>
                  {/* Seletor de modo de exibição */}
                  {idx === 0 && (
                    <Box display="flex" alignItems="center">
                      <Tooltip
                        title={
                          chartMode === CHART_MODE.STACKED
                            ? "Mostrar linhas separadas"
                            : "Mostrar valores somados"
                        }
                      >
                        <IconButton
                          onClick={toggleChartMode}
                          size="small"
                          sx={styles.toggleViewButton}
                        >
                          {chartMode === CHART_MODE.STACKED ? (
                            <ShowChart fontSize="small" />
                          ) : (
                            <StackedLineChart fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                <LineChartComponent
                  data={chartData}
                  isZoomEnabled={isZoomEnabled}
                  onZoomChange={(range) => {
                    // Use .unix() para interpretar o valor como segundos
                    const startDate = dayjs.unix(range?.start).tz(timeZone);
                    const endDate = dayjs.unix(range?.end).tz(timeZone);

                    handleAplicarZoom(startDate, endDate);
                  }}
                  stackedGraphKeys={
                    chartMode === CHART_MODE.STACKED ? selectedInterfaces : []
                  }
                  stackedGraphColors={stackedGraphColors}
                  objectColorsLine={
                    chartMode === CHART_MODE.MULTILINE
                      ? objectColorsLine
                      : null
                  }
                  lineChartOptions={{
                    margin: { top: 20, right: 30, left: 10, bottom: 0 },
                  }}
                  xOptions={{
                    tickFormatter: (clock) =>
                      dayjs.unix(clock).tz(timeZone).format("DD/MM HH:mm"),
                  }}
                />
              </Paper>
            </Grid>
          );
        })}

        {/* Tabela de interfaces - sempre full width */}
        <Grid item xs={12} sx={{ display: "flex", ...styles.gridContent }}>
          <Paper
            elevation={theme?.palette?.mode === "dark" ? 0 : 3}
            sx={styles.paperContent}
          >
            {!!loading && <LinearProgress sx={styles.linearProgress} />}
            {/* Título*/}
            <Box sx={styles.boxTitle}>
              <Box sx={styles.titleIcon}>
                <WifiOutlined color="primary" />
                <Typography>Links</Typography>
              </Box>
              <TextField
                size="small"
                variant="outlined"
                label="Buscar"
                placeholder="Buscar interfaces"
                value={search || ""}
                disabled={loading}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearch("")}
                        aria-label="Limpar busca"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>
            <Divider sx={{ mt: 1 }} />
            <Box sx={{ ...styles.paperContent }}>
              <DatagridComponent
                columns={columns}
                rows={filteredRows}
                options={{
                  checkboxSelection: true,
                  onRowSelectionModelChange: handleSelectionChange,
                  rowSelectionModel: rowSelectionModel,
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * Monta os datasets por direção de tráfego
 */
function buildDatasets(links, selectedInterfaces, direction) {
  return selectedInterfaces.map((iface) => ({
    key: iface,
    data: links?.[direction]?.[iface]?.history || [],
  }));
}

/**
 * Decide qual normalização aplicar de acordo com o modo
 */
function buildChartData(datasets, chartMode) {
  if (chartMode === CHART_MODE.CONCATENATED) {
    return concatenateSummedData(datasets);
  }
  return concatenateStackedData(datasets);
}

/**
 * Normaliza dados para gráficos STACKED ou MULTILINE
 */
function concatenateStackedData(datasets) {
  const map = new Map();

  datasets.forEach(({ key, data }) => {
    data?.forEach(({ clock, value }) => {
      if (!map.has(clock)) {
        map.set(clock, { clock });
      }
      map.get(clock)[key] = value;
    });
  });

  return Array.from(map.values()).sort((a, b) => a.clock - b.clock);
}

/**
 * Normaliza dados para modo CONCATENATED (soma total)
 */
function concatenateSummedData(datasets) {
  const map = new Map();

  datasets.forEach(({ data }) => {
    data?.forEach(({ clock, value }) => {
      if (!map.has(clock)) {
        map.set(clock, { clock, value: 0 });
      }
      map.get(clock).value += value || 0;
    });
  });

  return Array.from(map.values()).sort((a, b) => a.clock - b.clock);
}

/**
 * Ordena as chaves do primeiro item do chartData
 * com base no maior valor (max) de cada interface
 * A ordenação é decrescente (maior → menor)
 */
function sortChartDataKeysByMax(chartData, datasets, links, direction) {
  if (!chartData?.length || !datasets?.length) {
    return chartData;
  }

  const firstItem = chartData[0];

  // Extrai as chaves (interfaces), ignorando o clock
  const keys = Object.keys(firstItem).filter((k) => k !== "clock");

  // Ordena as chaves com base no max de cada interface
  const sortedKeys = keys.sort((a, b) => {
    const maxA = links?.[direction]?.[a]?.max ?? 0;
    const maxB = links?.[direction]?.[b]?.max ?? 0;
    return maxA - maxB;
  });

  // Reconstrói o primeiro objeto com a ordem correta
  const orderedFirstItem = { clock: firstItem.clock };
  sortedKeys.forEach((key) => {
    orderedFirstItem[key] = firstItem[key];
  });

  // Substitui apenas o primeiro item
  const newChartData = [...chartData];
  newChartData[0] = orderedFirstItem;

  return newChartData;
}

/**
 * Conversão de dados
 */
const formatBytes = ({ bytes, unidade = null, precisao = 2 }) => {
  if (bytes === 0 || bytes === "0") return "0 B";
  const k = 1000;
  const sizes =
    unidade === "pps"
      ? ["pps", "Kpps", "Mpps", "Gpps", "Tpps", "Ppps", "Epps"]
      : ["bps", "Kbps", "Mbps", "Gbps", "Tbps", "Pbps", "Ebps"];
  const i = Math?.floor(Math?.log(Number(bytes)) / Math?.log(k));
  return (
    parseFloat((Number(bytes) / Math?.pow(k, i))?.toFixed(precisao)) +
    " " +
    sizes[i]
  );
};
